import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ModalidadSeleccion,
  CreateModalidadSeleccionRequest,
  UpdateModalidadSeleccionRequest
} from '../models/modalidad-seleccion.interface';
import { environment } from '../../../../environments/environment';
import { ApiEnvelope } from '../../../shared/models/api-envelope';
import { ModalidadSeleccionMapper } from '../models/modalidad-seleccion.mapper';
import { ModalidadSeleccionDto } from '../models/modalidad-seleccion.dto';

@Injectable({
  providedIn: 'root'
})
export class ModalidadSeleccionService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseUrl.generic}/ModalidadDeSeleccion`;

  // Envelope común de la API
  private unwrapResponse<T>() {
    return map((response: T | ApiEnvelope<T>) => {
      const maybe = response as ApiEnvelope<T>;
      return maybe && (maybe as { data?: T }).data !== undefined
        ? (maybe.data as T)
        : (response as T);
    });
  }

  // Normaliza claves del backend -> modelo de UI
  private normalizeModalidadSeleccion(raw: ModalidadSeleccionDto): ModalidadSeleccion {
    return ModalidadSeleccionMapper.fromDto(raw);
  }

  private normalizeList(
    list: ModalidadSeleccionDto[] | ModalidadSeleccion[]
  ): ModalidadSeleccion[] {
    return ModalidadSeleccionMapper.fromDtoList(list as ModalidadSeleccionDto[]);
  }

  /**
   * Obtiene todas las modalidades de selección
   */
  getModalidadesSeleccion(): Observable<ModalidadSeleccion[]> {
    return this.http.get<ApiEnvelope<ModalidadSeleccionDto[]>>(`${this.baseUrl}/GetAll`).pipe(
      this.unwrapResponse<ModalidadSeleccionDto[]>(),
      map((res: ModalidadSeleccionDto[]) => this.normalizeList(res))
    );
  }

  /**
   * Obtiene una modalidad de selección por ID
   */
  getModalidadSeleccionById(id: number): Observable<ModalidadSeleccion> {
    const params = new HttpParams().set('id', id.toString());
    return this.http
      .get<ApiEnvelope<ModalidadSeleccionDto>>(`${this.baseUrl}/GetByID`, { params })
      .pipe(
        this.unwrapResponse<ModalidadSeleccionDto>(),
        map((res: ModalidadSeleccionDto) => this.normalizeModalidadSeleccion(res))
      );
  }

  /**
   * Crea una nueva modalidad de selección
   */
  createModalidadSeleccion(
    modalidadSeleccion: CreateModalidadSeleccionRequest
  ): Observable<ModalidadSeleccion> {
    return this.http
      .post<ApiEnvelope<ModalidadSeleccionDto>>(`${this.baseUrl}/Create`, modalidadSeleccion)
      .pipe(
        this.unwrapResponse<ModalidadSeleccionDto>(),
        map((res: ModalidadSeleccionDto) => this.normalizeModalidadSeleccion(res))
      );
  }

  /**
   * Actualiza una modalidad de selección existente
   */
  updateModalidadSeleccion(
    id: number,
    modalidadSeleccion: UpdateModalidadSeleccionRequest
  ): Observable<boolean> {
    return this.http
      .put<ApiEnvelope<boolean>>(`${this.baseUrl}/${id}`, modalidadSeleccion)
      .pipe(this.unwrapResponse<boolean>());
  }

  /**
   * Activa o desactiva una modalidad de selección
   */
  toggleModalidadSeleccionStatus(id: number, isActive: boolean): Observable<boolean> {
    return this.http
      .patch<ApiEnvelope<boolean>>(`${this.baseUrl}/${id}/${isActive}`, {})
      .pipe(this.unwrapResponse<boolean>());
  }
}
